import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  COMPETENCIES,
  PHASES,
  TIER_MODELS,
  scoreColor,
  scoreOf,
  tierAverage,
  phaseAverage,
} from "@/data/coachingData";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, Building2, Users, Users2 } from "lucide-react";

export function ScorecardInput({
  modelType,
  teamScores,
  setTeamScore,
  teamEnabled,
  toggleTeam,
  onModelChange,
  companyName,
  setCompanyName,
  context,
  setContext,
  scores,
  setScore,
  headcounts,
  setHeadcount,
  onFinish,
  loading,
}) {
  const { t } = useI18n();
  const tiers = TIER_MODELS[modelType];
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState({});

  const total = tiers.length + 1;
  const current = Math.min(step, total - 1);
  const pct = Math.round(((current + 1) / total) * 100);

  const pick = (tierKey, compKey, agree) => {
    setScore(tierKey, compKey, agree);
    setTouched((p) => ({ ...p, [`${tierKey}.${compKey}`]: true }));
  };

  const next = () => {
    if (current === total - 1) onFinish();
    else setStep(current + 1);
  };

  const tier = current > 0 ? tiers[current - 1] : null;
  const answered = tier
    ? COMPETENCIES.filter((c) => touched[`${tier.key}.${c.key}`]).length
    : 0;
  const avg = tier ? tierAverage(scores, tier.key).toFixed(1) : 0;

  return (
    <div className="max-w-3xl mx-auto" data-testid="guided-assessment">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="uppercase tracking-widest font-semibold text-amber-600" data-testid="step-counter">
            {t("step.counter", { current: current + 1, total })}
          </span>
          <span className="text-slate-500 tabular-nums">{t("step.percent", { pct })}</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-amber-400"
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            data-testid="step-progress-bar"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
        >
          <Card className="bg-white border-slate-200 p-6 sm:p-8">
            {current === 0 ? (
              <div className="space-y-6" data-testid="step-company">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-600">
                    <Building2 size={20} />
                  </span>
                  <div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
                      {t("step.companyTitle")}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{t("step.companyDesc")}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600 mb-1.5 block">
                    {t("step.companyLabel")}
                  </Label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t("step.companyPlaceholder")}
                    className="bg-white border-slate-300 text-slate-900"
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600 mb-1.5 block">
                    {t("step.modelLabel")}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {["4-tier", "6-tier"].map((m) => (
                      <button
                        key={m}
                        onClick={() => onModelChange(m)}
                        data-testid={`model-${m}`}
                        className={`rounded-xl border p-3.5 text-left transition-colors duration-200 ${
                          modelType === m
                            ? "border-indigo-500 bg-indigo-500/10"
                            : "border-slate-300 bg-white hover:border-slate-400"
                        }`}
                      >
                        <p className="text-sm font-semibold text-slate-900">{t(`model.${m}.label`)}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {TIER_MODELS[m].map((x) => t(`tierShort.${x.key}`)).join(" · ")}
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">{t("step.modelHint")}</p>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600 mb-1.5 block">
                    {t("step.contextLabel")}
                  </Label>
                  <Textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder={t("step.contextPlaceholder")}
                    rows={3}
                    className="bg-white border-slate-300 text-slate-900"
                    data-testid="context-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1.5">{t("step.contextHint")}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6" data-testid={`step-tier-${tier.key}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-600 font-mono text-sm font-semibold">
                      {current}
                    </span>
                    <div>
                      <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
                        {t(`tier.${tier.key}`)}
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">{t("step.tierDesc")}</p>
                    </div>
                  </div>
                  <Badge className="bg-slate-100 text-slate-700 border-slate-300 font-mono">
                    {t("step.avg", { value: avg })}
                  </Badge>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                  <Users size={17} className="text-amber-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{t("step.headcount")}</p>
                    <p className="text-[11px] text-slate-500">{t("step.headcountHint")}</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={headcounts[tier.key] ?? ""}
                    placeholder="1"
                    onChange={(e) => setHeadcount(tier.key, e.target.value)}
                    className="w-20 h-9 rounded-md bg-white border border-slate-300 text-slate-900 text-sm px-2 focus:outline-none focus:border-indigo-500"
                    data-testid={`headcount-${tier.key}`}
                  />
                </div>

                <div className="space-y-6">
                  {PHASES.map((phase) => (
                    <div key={phase.key} data-testid={`phase-${phase.key}-${tier.key}`}>
                      <div className="flex items-end justify-between gap-3 border-b border-slate-200 pb-2 mb-3.5">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{t(`phase.${phase.key}.title`)}</p>
                          <p className="text-[11px] text-slate-500">{t(`phase.${phase.key}.sub`)}</p>
                        </div>
                        <span
                          className="text-[11px] font-mono shrink-0"
                          style={{ color: scoreColor(phaseAverage(scores, tier.key, phase.key)) }}
                        >
                          {t("step.phaseAvg", { value: phaseAverage(scores, tier.key, phase.key).toFixed(1) })}
                        </span>
                      </div>

                      <div className="space-y-5">
                        {phase.comps.map((comp) => {
                          const val = scoreOf(scores, tier.key, comp.key);
                          return (
                            <div key={comp.key}>
                              <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[9px] uppercase tracking-widest font-bold text-indigo-600/80 shrink-0">
                                    {t(`dim.${comp.dim}`)}
                                  </span>
                                  <p className="text-sm font-semibold text-slate-900">{t(`comp.${comp.key}`)}</p>
                                </div>
                                <span
                                  className="font-mono text-sm font-semibold tabular-nums"
                                  style={{ color: scoreColor(val) }}
                                  data-testid={`score-value-${tier.key}-${comp.key}`}
                                >
                                  {val}
                                </span>
                              </div>
                              <p className="text-xs text-slate-600 mb-2 leading-snug">{t(`q.${comp.key}`)}</p>
                              <div className="grid grid-cols-5 gap-1.5">
                                {[1, 2, 3, 4, 5].map((a) => (
                                  <button
                                    key={a}
                                    type="button"
                                    onClick={() => pick(tier.key, comp.key, a)}
                                    data-testid={`guided-${tier.key}-${comp.key}-${a}`}
                                    className={`h-auto min-w-0 py-2 px-1 rounded-lg text-[11px] font-medium leading-tight break-words transition-colors duration-200 ${
                                      val === a
                                        ? "bg-indigo-600 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800"
                                    }`}
                                  >
                                    {t(`agree.${a}`)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!teamEnabled[tier.key]}
                      onChange={() => toggleTeam(tier.key)}
                      className="mt-0.5 h-4 w-4 accent-indigo-500 shrink-0"
                      data-testid={`team-toggle-${tier.key}`}
                    />
                    <span>
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Users2 size={15} className="text-amber-600" /> {t("t360.enable")}
                      </span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">{t("t360.enableHint")}</span>
                    </span>
                  </label>

                  {teamEnabled[tier.key] && (
                    <div className="mt-4 space-y-4" data-testid={`team-pass-${tier.key}`}>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
                        {t("t360.teamView")}
                      </p>
                      {PHASES.map((phase) => (
                        <div key={phase.key}>
                          <p className="text-[11px] font-semibold text-slate-600 mb-2">
                            {t(`phase.${phase.key}.title`)}
                          </p>
                          <div className="space-y-2">
                            {phase.comps.map((comp) => {
                              const tv = scoreOf(teamScores, tier.key, comp.key);
                              return (
                                <div key={comp.key} className="flex items-center gap-3 flex-wrap">
                                  <span className="text-xs text-slate-700 w-full sm:w-52 shrink-0 truncate">
                                    {t(`comp.${comp.key}`)}
                                  </span>
                                  <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((a) => (
                                      <button
                                        key={a}
                                        type="button"
                                        onClick={() => setTeamScore(tier.key, comp.key, a)}
                                        data-testid={`team-${tier.key}-${comp.key}-${a}`}
                                        title={t(`agree.${a}`)}
                                        className={`h-7 w-7 rounded-md text-[11px] font-mono font-semibold transition-colors duration-200 ${
                                          tv === a
                                            ? "bg-amber-500 text-slate-900"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800"
                                        }`}
                                      >
                                        {a}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-slate-500">
                  {t("step.answered", { n: answered, total: COMPETENCIES.length })}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(0, current - 1))}
                disabled={current === 0}
                className="bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                data-testid="step-back-btn"
              >
                <ArrowLeft size={15} className="mr-1.5" /> {t("step.back")}
              </Button>
              <span className="hidden sm:block text-[11px] text-slate-500">{t("step.reviewHint")}</span>
              <Button
                onClick={next}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
                data-testid={current === total - 1 ? "step-finish-btn" : "step-next-btn"}
              >
                {current === total - 1 ? (
                  loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin mr-1.5" /> {t("app.generating")}
                    </>
                  ) : (
                    <>
                      <Sparkles size={15} className="mr-1.5" /> {t("step.finish")}
                    </>
                  )
                ) : (
                  <>
                    {t("step.next")} <ArrowRight size={15} className="ml-1.5" />
                  </>
                )}
              </Button>
            </div>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
